import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  useTranslationContext,
} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useEffect, useRef, useState} from 'react';

import {suffixTestId, type TestIdProps} from 'shared/web';

import type {Preset} from '../../../../types/Api';
import {PRESET_COLORS} from '../../constants/presetColors';
import {resolvePresetColor} from '../../utils/resolvePresetColor';
import {getEditPresetSchema} from './getEditPresetSchema';

type PartialPresetType = {
  title: string;
  description?: string | null;
};

export type EditPresetDialogProps = TestIdProps & {
  presetToEdit?: Preset;
  onSubmit: (title: string, desc: string, color?: string) => Promise<void>;
  /** Current color of the preset being edited. */
  initialColor?: string;
  /**
   * Live preview: called immediately with every color the user picks, so the
   * preset tab behind the dialog updates on the spot. When the dialog closes
   * without a successful save (Discard, X, ESC, overlay click), it is called
   * once more with `initialColor` to revert the preview.
   */
  onColorPreview?: (color: string) => void;
};

export function EditPresetDialog(props: EditPresetDialogProps) {
  const t = useTranslationContext();
  const [selectedColor, setSelectedColor] = useState(
    props.initialColor ? resolvePresetColor(props.initialColor) : ''
  );
  const hasSavedRef = useRef(false);
  const hasPreviewedRef = useRef(false);

  const PartialPresetTypeSchema = getEditPresetSchema(t);

  const handleColorChange = (color: string) => {
    setSelectedColor(color);
    hasPreviewedRef.current = true;
    props.onColorPreview?.(color);
  };

  const handleSubmit = (values: PartialPresetType) =>
    props.onSubmit(values.title, values.description ?? '', selectedColor).then(() => {
      hasSavedRef.current = true;
      closeCurrentDialog();
    });

  // Revert an unsaved live color preview when the dialog goes away by ANY path
  // (Discard, X, ESC, overlay click). openDialog exposes no onClose hook
  // (ManagedDialogProps omits it), so unmount is the only signal covering them all.
  // eslint-disable-next-line eslint-js/no-restricted-syntax -- intentional: unmount-only cleanup reverting an unsaved color preview; useOnMount cannot register cleanups
  useEffect(
    function revertColorPreviewOnUnmount() {
      return () => {
        if (hasPreviewedRef.current && !hasSavedRef.current) {
          props.onColorPreview?.(props.initialColor ?? '');
        }
      };
    },
    [props.onColorPreview, props.initialColor] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  return (
    <Form<PartialPresetType>
      experimentalZodSchema={PartialPresetTypeSchema}
      onSubmit={handleSubmit}
      defaultValues={{
        title: props.presetToEdit?.title,
        description: props.presetToEdit?.description,
      }}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            isRequired
            type="text"
            name="title"
            label={t('general.presets.presetName')}
            placeholder={t('general.presets.presetName')}
            data-testid={suffixTestId('presetName', props)}
          />

          <FormField
            control={control}
            type="textarea"
            name="description"
            label={t('general.presets.descriptionPlaceholder')}
            placeholder={t('general.presets.descriptionPlaceholder')}
          />

          <ColorPicker
            value={selectedColor}
            onChange={handleColorChange}
            noColorLabel={t('general.labels.noColor')}
            data-testid={suffixTestId('colorPicker', props)}
          />

          <ButtonGroup align="right">
            <Button
              variant="secondary"
              title={t('general.actions.discard')}
              onClick={closeCurrentDialog}
            />
            <FormButton
              control={control}
              type="submit"
              title={t('general.actions.save')}
              data-testid={suffixTestId('submitButton', props)}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}

// ── Color picker ──────────────────────────────────────────────────────

type ColorPickerProps = {
  value: string;
  onChange: (color: string) => void;
  noColorLabel: string;
  'data-testid'?: string;
};

function ColorPicker(props: ColorPickerProps) {
  const isNone = !props.value;

  return (
    <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}} data-testid={props['data-testid']}>
      {/* No-color option */}
      <button
        type="button"
        onClick={() => props.onChange('')}
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: '#EBECF0',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          boxShadow: isNone ? '0 0 0 2px #fff, 0 0 0 4px #8993A4' : 'none',
          transition: 'box-shadow 100ms',
          position: 'relative',
          overflow: 'hidden',
        }}
        aria-label={props.noColorLabel}
        aria-pressed={isNone}
      >
        {/* Diagonal line to indicate "none" */}
        <span
          style={{
            position: 'absolute',
            top: '50%',
            left: -2,
            width: 32,
            height: 2,
            background: '#8993A4',
            transform: 'rotate(-45deg)',
            transformOrigin: 'center',
          }}
        />
      </button>

      {PRESET_COLORS.map((color) => (
        <button
          key={color}
          type="button"
          onClick={() => props.onChange(color)}
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: color,
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            boxShadow: props.value === color ? `0 0 0 2px #fff, 0 0 0 4px ${color}` : 'none',
            transition: 'box-shadow 100ms',
          }}
          aria-label={color}
          aria-pressed={props.value === color}
        />
      ))}
    </div>
  );
}
